import { selectCurrency } from 'app/modules/auth'
import { clientTimeZoneOffset, FLOW_TYPE, PaymentMethod, PaymentStatus, Languages, DEFAULT_WORKER } from 'app/utils/common/constants'
import { MajorServiceModel } from 'app/utils/common/models'
import { KTSVG } from 'app/utils/helpers'
import { FieldSelector, FieldServiceInput, FieldTextInput, IMainServiceSelected } from 'app/utils/helpers/components/Form'
import { transformPackageInfo, transformWorkerData } from 'app/utils/helpers/OrderDrawerHelpers'
import { FormikProps } from 'formik'
import i18next from 'i18next'
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react'
import { Button, Col, Row } from 'react-bootstrap-v5'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { Response } from 'setup/axios/HttpResponse'
import { HTTP_RESPONSE_STATUS } from 'setup/constants/Http'
import { ToastDefaultConfig } from 'setup/toast/ToastConfig'
import { OrderSourceSelect, PriceDetails } from '../../models'
import { MultiWorker, PackageServiceResponse, PackageInfo } from '../../models/OrderDrawerModel'
import { ticketApi } from '../../redux/TicketApi'
import {
  loadPackageCalendarV2,
  startPackageAvailabilityV2,
  buildLegacyPackageInfoFromDetails,
  buildPreSelectSPFromCalendar,
  computeCalendarLimitDays,
} from '../../redux/PackageAvailabilityV2'
import AccordionItem from './AccordionItem'
import { useAppSelector } from 'setup/redux/Hooks'
import { IForm } from './type'
import { defaultPackage, defaultMainService, initalTouchedPackage, initalTouchedMainService } from 'app/utils/common/constants/CreateOrderFormConfig'
import { _debounce } from 'app/utils/helpers/extendMethod'
import { CentroOrderSourceModel } from '../../models/TicketAttendanceDelivery'
export interface OptionServiceMerge extends MajorServiceModel {
    packageWorkFlowId: number | null
}

export const priceSubSubService = (priceDetails: PriceDetails[], subSubService: { id: number, quantity?: number }) => {
    const { id, quantity = 1 } = subSubService
    const _priceDetails = priceDetails.filter(e => e.subSubServiceId == id);
    return _priceDetails.length == 0 ? ''
        : quantity === 1 ? _priceDetails[0].price
            : (
                _priceDetails.find(e => e.quantityFrom <= quantity && quantity <= e.quantityTo)?.price ??
                (
                    quantity > _priceDetails[_priceDetails.length - 1].quantityTo ?
                        _priceDetails[_priceDetails.length - 1].price
                        : i18next.t('CREATE_TICKET_MODAL.UPON_CHECKUP') as string
                )
            );
}

const NeedServiceComponent = (props: {
    formProps: FormikProps<IForm>,
    rtl?: boolean,
    language: string,
    index?: number,
    centroOrderSourceOption: OrderSourceSelect[],
}) => {
    const { formProps: { values, setFieldValue, setValues, setTouched, setFieldTouched }, language, rtl = false, index = 0, centroOrderSourceOption } = props;
    const { t } = useTranslation();
    const currency = useAppSelector(selectCurrency);
    const invoiceInputRef = useRef<HTMLInputElement>(null)
    const [invoiceFileError, setInvoiceFileError] = useState<string>('')
    const [invoicePreviewUrl, setInvoicePreviewUrl] = useState<string>('')
    const [invoicePreviewType, setInvoicePreviewType] = useState<'image' | 'pdf' | ''>('')
    const [state, setState] = useState<{
        loadingPackage: boolean,
        workerConfig: MultiWorker
    }>({
        loadingPackage: false,
        workerConfig: {
            isApply: false,
            isRequireMatchWorkerNumber: true,
            minWorkerAllow: 0
        }
    })
    // Track previous package to detect changes
    const prevPackageRef = useRef<number | undefined>(undefined)
    const packageFetchRequestRef = useRef(0)
    const prevPaymentMethodRef = useRef<string>(values.payment_method ?? '')
    const isInvoicePayment = values.payment_method === 'Invoice'
    const isOnlinePaymentLinkSms = values.payment_method === 'OnlinePaymentLinkSms'
    const maxInvoiceFileBytes = 5 * 1024 * 1024
    const allowedInvoiceTypes = ['image/jpeg', 'image/png', 'application/pdf', 'image/jpg']
    const allowedInvoiceExtensions = ['.jpg', '.jpeg', '.png', '.pdf']

    const clearInvoicePreview = () => {
        if (invoicePreviewUrl) {
            URL.revokeObjectURL(invoicePreviewUrl)
        }
        setInvoicePreviewUrl('')
        setInvoicePreviewType('')
    }

    const clearInvoiceFile = () => {
        clearInvoicePreview()
        setInvoiceFileError('')
        setFieldValue('invoiceFile', null, false)
        if (invoiceInputRef.current) {
            invoiceInputRef.current.value = ''
        }
    }

    const validateInvoiceFile = (file: File): string | null => {
        const extension = `.${file.name.split('.').pop() || ''}`.toLowerCase()
        if (!allowedInvoiceTypes.includes(file.type) || !allowedInvoiceExtensions.includes(extension)) {
            return t('CREATE_ORDER_DRAWER.INVOICE_UPLOAD_INVALID_TYPE_SUPPORTED')
        }

        if (file.size > maxInvoiceFileBytes) {
            return t('CREATE_ORDER_DRAWER.INVOICE_UPLOAD_SIZE_EXCEED')
        }

        return null
    }

    const handleInvoiceFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        if (!file) return

        const error = validateInvoiceFile(file)
        if (error) {
            setInvoiceFileError(error)
            clearInvoicePreview()
            setFieldValue('invoiceFile', null, false)
            if (invoiceInputRef.current) {
                invoiceInputRef.current.value = ''
            }
            return
        }

        setInvoiceFileError('')
        clearInvoicePreview()
        const previewUrl = URL.createObjectURL(file)
        setInvoicePreviewUrl(previewUrl)
        setInvoicePreviewType(file.type === 'application/pdf' ? 'pdf' : 'image')
        setFieldValue('invoiceFile', file, false)
        setFieldValue('payment_status', 'paid', false)
    }

    const handleViewInvoiceFile = () => {
        if (invoicePreviewUrl) {
            window.open(invoicePreviewUrl, '_blank')
        }
    }

    useEffect(() => {
        if (!isInvoicePayment) {
            clearInvoiceFile()
        }
    }, [isInvoicePayment])

    useEffect(() => {
        const previousMethod = prevPaymentMethodRef.current
        if (isInvoicePayment && previousMethod !== 'Invoice') {
            if (!values.payment_status || values.payment_status === 'invoice') {
                setFieldValue('payment_status', 'pending', false)
            }
        }

        if (isOnlinePaymentLinkSms && values.payment_status !== 'pending') {
            setFieldValue('payment_status', 'pending', false)
        }

        prevPaymentMethodRef.current = values.payment_method ?? ''
    }, [isInvoicePayment, isOnlinePaymentLinkSms, setFieldValue, values.payment_method, values.payment_status])

    const onChangeMainService = (service: IMainServiceSelected) => {
        if (service) {
            setValues(s => ({
                ...s,
                main_service: service,
                flow_type: service.type === 'package_service' ? FLOW_TYPE.PACKAGE : '',
                service_type: service.type,
                ...defaultMainService
            }))
        }

        setTouched({ ...props.formProps.touched, ...initalTouchedMainService })
    }

    var valuesRef = useRef<IForm>(values);
    valuesRef.current = values;

    const onChangeNumberWorker = useCallback(_debounce(async (numberOfWorker: number) => {
        var values = valuesRef.current;
        if (values.main_service && values.main_service.type === 'package_service' && values.maxWorker && numberOfWorker !== 0 && numberOfWorker <= values.maxWorker) {
            const requestId = ++packageFetchRequestRef.current
            const oldSP = values.preSelectSP
            setValues(s => ({
                ...s,
                loadingType: 'worker',
                isOutOfCapacity: false,
                preSelectSP: undefined,
                date_time: undefined,
                worker: undefined
            }))
            const package_selected: PackageInfo | undefined = values.listPackages.find((s: PackageInfo) => s.id === values.package)
            if (package_selected) {
                try {
                    // V2: refetch calendar only — package details didn't change, just numbOfSelectWorker.
                    // Reuse the currently-loaded window so the calendar grid doesn't shrink.
                    const { legacy: data } = await loadPackageCalendarV2(
                        {
                            packageWorkflowId: values.main_service?.data?.packageWorkFlowId || 0,
                            countryId: values.location?.countryId || 0,
                            packageId: package_selected.id,
                            majorId: values.main_service.id,
                            clientTimeOffset: -clientTimeZoneOffset,
                            companyId: package_selected.preSelectSP?.companyId || '',
                            customerId: values.customer_id,
                            customerPhone: values.phone,
                            customerLat: values.location?.lat,
                            customerLng: values.location?.lng,
                            spIds: values.spIds,
                            zoneIds: values.zoneIds,
                            workerId: values.worker && values.worker !== DEFAULT_WORKER ? values.worker : undefined,
                            numbOfSelectWorker: numberOfWorker,
                            limitDays: values.calendarLoadedUntilDay ?? computeCalendarLimitDays(new Date(), new Date()),
                        },
                        values.packageDetailsV2 ?? null
                    )
                    if (data.packageInfo.length === 0) {
                        toast.error(`${t('ERROR_MESSAGE.PACKAGE_ERROR.CAPACITY_ERROR', { num: numberOfWorker })}`, ToastDefaultConfig());
                        if (packageFetchRequestRef.current !== requestId) return
                        setValues(s => ({
                            ...s,
                            total_price: Number(values.total_price),
                            without_vat: Number(values.without_vat),
                            vat: Number(values.vat),
                            number_of_workers: values.number_of_workers,
                            loadingType: undefined,
                            preSelectSP: oldSP,
                        }))
                        return

                    }
                    const PackagesInfo = transformPackageInfo(data.packageInfo)
                    const newPackageInfo: PackageInfo | undefined = PackagesInfo.find((s: PackageInfo) => s.id === values.package)
                    if (newPackageInfo) {
                        if (packageFetchRequestRef.current !== requestId) return
                        setValues(s => {
                            // Merge API result into existing list so dropdown still shows all packages
                            const prevList = s.listPackages || []
                            const merged = prevList.map((p) => {
                                const updated = PackagesInfo.find((x: PackageInfo) => x.id === p.id)
                                return updated ?? p
                            })
                            const added = PackagesInfo.filter((x: PackageInfo) => !prevList.some((p) => p.id === x.id))
                            const listPackages = added.length ? [...merged, ...added] : merged
                            return {
                                ...s,
                                listPackages,
                                preSelectSP: newPackageInfo.preSelectSP,
                                total_price: (newPackageInfo.packagePrice * numberOfWorker * (1 + values.percentVAT)).toFixed(2),
                                without_vat: (newPackageInfo.packagePrice * numberOfWorker).toFixed(2),
                                vat: (newPackageInfo.packagePrice * numberOfWorker * values.percentVAT).toFixed(2),
                                number_of_workers: numberOfWorker,
                                loadingType: undefined,
                            }
                        })
                    } else {
                        setValues(s => ({
                            ...s,
                            total_price: Number(values.total_price),
                            without_vat: Number(values.without_vat),
                            vat: Number(values.vat),
                            number_of_workers: values.number_of_workers,
                            preSelectSP: oldSP,
                            loadingType: undefined,
                        }))
                        toast.error(`${t('ERROR_MESSAGE.PACKAGE_ERROR.UNEXPECTED_ERROR')}`, ToastDefaultConfig());
                    }
                } catch (error) {
                    setValues(s => ({
                        ...s,
                        total_price: Number(values.total_price),
                        without_vat: Number(values.without_vat),
                        vat: Number(values.vat),
                        number_of_workers: values.number_of_workers,
                        preSelectSP: oldSP,
                        loadingType: undefined,
                    }))
                    toast.error(`${t('ERROR_MESSAGE.PACKAGE_ERROR.UNEXPECTED_ERROR')}`, ToastDefaultConfig());
                }
            }
        }
    }, 300), [values.loadingType])

    const fetchData = async () => {
        if (values.main_service && values?.package) {
            const requestId = ++packageFetchRequestRef.current
            // Clear worker IMMEDIATELY before any API calls to prevent race conditions
            setFieldValue('worker', undefined, false)

            setValues(s => ({
                ...s,
                loadingType: 'worker',
                isOutOfCapacity: false,
                ...defaultPackage,
                order_source: s.order_source,
                worker: undefined,
            }))
            setTouched({ ...props.formProps.touched, ...initalTouchedPackage })

            const package_selected: PackageInfo | undefined = values.listPackages.find((s: PackageInfo) => s.id === values.package)

            if (package_selected) {
                // V2 progressive load: fire details + calendar in parallel, render each as it arrives.
                // Phase 1 (details) paints package metadata, price, workers, and pricing immediately.
                // Phase 2 (calendar) fills preSelectSP + base dayAvailable so the Date step unlocks.
                const countryId = values.location?.countryId || 0
                // Initial calendar window covers only the current month's visible grid (~35-42 days).
                // The DateTimePackage's onMonthChange handler will lazily expand as the user navigates.
                const initialLimitDays = computeCalendarLimitDays(new Date(), new Date())
                const { detailsPromise, calendarPromise } = startPackageAvailabilityV2({
                    packageWorkflowId: values.main_service?.data?.packageWorkFlowId || 0,
                    countryId,
                    packageId: package_selected.id,
                    majorId: values.main_service.id,
                    clientTimeOffset: -clientTimeZoneOffset,
                    companyId: package_selected.preSelectSP?.companyId || '',
                    customerId: values.customer_id,
                    customerPhone: values.phone,
                    customerLat: values.location?.lat,
                    customerLng: values.location?.lng,
                    spIds: values.spIds,
                    zoneIds: values.zoneIds,
                    limitDays: initialLimitDays,
                })

                // --- Phase 1: details -----------------------------------------------------------
                const detailsHandled = detailsPromise.then((details) => {
                    if (packageFetchRequestRef.current !== requestId) return null
                    if (!details) return null

                    const PackagesInfo = transformPackageInfo(buildLegacyPackageInfoFromDetails(details))
                    const workers = transformWorkerData(PackagesInfo?.[0]?.workers || [])
                    const newPackageInfo: PackageInfo | undefined = PackagesInfo.find((s: PackageInfo) => s.id === values.package)
                    if (!newPackageInfo) return null

                    const numberOfWorker = newPackageInfo.multiWorker.minWorkerAllow
                    const packageVAT = newPackageInfo.percentVAT
                    const number_visit = newPackageInfo.numberOfVisitPerWeek || 0
                    const number_week = newPackageInfo.numberOfWeeks || 0

                    // isShowVat lives on workers (any worker in scope mirrors the country/company flag),
                    // so we can compute final pricing here without waiting for the calendar phase.
                    const isShowVat = details.workers?.[0]?.isShowVat ?? false
                    const packagePrice = newPackageInfo.packagePriceWithVat / (1 + packageVAT)
                    const packagePriceWithVat = isShowVat && packageVAT ? newPackageInfo.packagePriceWithVat : packagePrice

                    const disabledWorker = !newPackageInfo.multiWorker.isApply || (newPackageInfo.multiWorker.isApply && newPackageInfo.multiWorker.isRequireMatchWorkerNumber)

                    setValues(s => {
                        const prevList = s.listPackages || []
                        const merged = prevList.map((p) => {
                            const updated = PackagesInfo.find((x: PackageInfo) => x.id === p.id)
                            return updated ?? p
                        })
                        const added = PackagesInfo.filter((x: PackageInfo) => !prevList.some((p) => p.id === x.id))
                        const listPackages = added.length ? [...merged, ...added] : merged
                        return {
                            ...s,
                            listPackages,
                            number_of_workers: numberOfWorker,
                            total_price: disabledWorker ? packagePriceWithVat.toFixed(2) : (packagePriceWithVat * numberOfWorker).toFixed(2),
                            without_vat: disabledWorker ? packagePrice.toFixed(2) : (packagePrice * numberOfWorker).toFixed(2),
                            vat: disabledWorker ? (packagePriceWithVat - packagePrice).toFixed(2) : ((packagePriceWithVat - packagePrice) * numberOfWorker).toFixed(2),
                            numberOfWeeks: newPackageInfo.numberOfWeeks,
                            numberOfVisitPerWeek: newPackageInfo.numberOfVisitPerWeek,
                            // preSelectSP intentionally NOT set here; calendar phase fills it.
                            workerDayAvailable: undefined,
                            workerConfig: newPackageInfo.multiWorker,
                            maxWorker: newPackageInfo.multiWorker.workerNum,
                            totalVisit: number_week * number_visit,
                            isShowDeliveryTime: newPackageInfo.isShowDeliveryTime,
                            worker_available: workers,
                            percentVAT: packageVAT,
                            favWorkerIds: details.workers?.filter(w => w.isFav).map(w => w.workerId) ?? [],
                            worker: undefined,
                            packageServicePriceSetId: newPackageInfo.packageServicePriceSetId || null,
                            packageServicePriceSetDetailId: newPackageInfo.packageServicePriceSetDetailId || null,
                            isRefund: newPackageInfo.isRefund,
                            reScheduleLimit: newPackageInfo.reScheduleLimit || null,
                            subServiceId: newPackageInfo?.subServiceId || 0,
                            numberOfVisitRecurrence: newPackageInfo.numberOfVisitRecurrence || 0,
                            packageDetailsV2: details,
                        }
                    })

                    setState((s) => ({ ...s, workerConfig: newPackageInfo.multiWorker }))

                    return details
                })

                // --- Phase 2: calendar ----------------------------------------------------------
                const calendarHandled = Promise.all([calendarPromise, detailsHandled])
                    .then(([calendar, details]) => {
                        if (packageFetchRequestRef.current !== requestId) return
                        const preSelectSP = buildPreSelectSPFromCalendar(calendar, details, countryId)
                        const firstAvailableDate = preSelectSP?.dayAvailable?.find(d => d.isAvailable)?.dateCheck
                        const workerStartDateById = new Map<string, Date | undefined>(
                            (calendar?.workerStartDates || []).map(w => [
                                w.workerId,
                                (w.firstAvailableDate ?? undefined) as unknown as Date | undefined,
                            ])
                        )
                        setValues(s => ({
                            ...s,
                            preSelectSP,
                            preSelectSPBaseDayAvailable: preSelectSP?.dayAvailable || [],
                            preSelectSPBaseRouteOptimizationCfg: preSelectSP?.routeOptimizationCfg ?? null,
                            preSelectSPBaseVisitReminder: preSelectSP?.visitReminder ?? '',
                            hasClusterRouteOptimization: calendar?.hasClusterRouteOptimization ?? false,
                            calendarLoadedUntilDay: initialLimitDays,
                            worker_available: (s.worker_available || []).map(w =>
                                'workerId' in w
                                    ? { ...w, startDate: workerStartDateById.get(w.workerId) ?? firstAvailableDate }
                                    : w
                            ),
                        }))
                    })

                try {
                    await Promise.all([detailsHandled, calendarHandled])
                } catch (error) {
                    toast.error(`${t('ERROR_MESSAGE.DATE_TIME_ERROR.UNEXPECTED_ERROR')}`, ToastDefaultConfig())
                } finally {
                    if (packageFetchRequestRef.current === requestId) {
                        setValues(s => ({ ...s, loadingType: undefined }))
                    }
                }
            } else if (packageFetchRequestRef.current === requestId) {
                setValues(s => ({ ...s, loadingType: undefined }))
            }
        }
    }

    const handleSearchPackage = useCallback(_debounce(async (searchText: string) => {
        if (!values.main_service || values.main_service.type !== 'package_service') return;
        
        setState(s => ({ ...s, loadingPackage: true }));
        try {
            const { data, status } = await ticketApi.searchPackageAvailable(
                values.main_service.data.packageWorkFlowId || 0,
                searchText,
                values.location?.lat,
                values.location?.lng,
                values.main_service?.id
            );

            if (status !== HTTP_RESPONSE_STATUS.SUCCESS) {
                toast.error(`${t('ERROR_MESSAGE.PACKAGE_ERROR.UNEXPECTED_ERROR')}`, ToastDefaultConfig());
                setState(s => ({ ...s, loadingPackage: false }));
                return;
            }

            const searchResults = data || [];
            
            // Map the PackageSearchItemDTO to our internal PackageInfo structure 
            // enough to be displayed in the dropdown
            const PackagesInfo: any[] = searchResults.map(item => ({
                id: item.id,
                titleAr: item.titleAr,
                titleEn: item.titleEn,
                multiWorker: { isApply: false, isRequireMatchWorkerNumber: false, minWorkerAllow: 0 },
                preSelectSP: undefined,
                workers: [],
                hasUnratedWorker: false,
                packagePrice: 0,
                packagePriceWithVat: 0,
                percentVAT: 0,
                isShowDeliveryTime: false
            }));

            setState(s => ({ ...s, loadingPackage: false }));
            setValues(s => ({
                ...s,
                listPackages: PackagesInfo as PackageInfo[],
                // Keep favWorkerIds intact if we still need them, but search API doesn't return them.
                // We'll rely on the later getPackageAvailable to fetch favWorkerIds.
            }));
            
            if (!PackagesInfo.length) setFieldTouched('package', true);
            
        } catch (e) {
            toast.error(`${t('ERROR_MESSAGE.PACKAGE_ERROR.UNEXPECTED_ERROR')}`, ToastDefaultConfig());
            setState(s => ({ ...s, loadingPackage: false }));
        }
    }, 500), [values.main_service]);

    useEffect(() => {
        if (values.main_service && values.main_service.type === 'package_service') {
            handleSearchPackage("");
        }
    }, [values.main_service, values.customer_id])

    // Unselect worker + base-on before fetchData when package changes.
    useEffect(() => {
        if (values.package && values.main_service?.type === 'package_service') {
            if (prevPackageRef.current !== undefined && prevPackageRef.current !== values.package) {
                packageFetchRequestRef.current++
                setValues((s) => ({
                    ...s,
                    loadingType: 'worker',
                    preSelectSP: undefined,
                    preSelectSPBaseDayAvailable: undefined,
                    worker_available: [],
                    worker: undefined,
                    workerDayAvailable: undefined,
                    workerDayAvailableWorkerId: undefined,
                    selectedWorkerRouteOptimizationCfg: undefined,
                    selectedWorkerVisitReminder: undefined,
                    selectedDateRouteOptimizationCfg: undefined,
                    selectedDateVisitReminder: undefined,
                    hasClusterRouteOptimization: false,
                    date_time: undefined,
                }))
                setFieldTouched('worker', false)
                setFieldTouched('date_time', false)
            }
            prevPackageRef.current = values.package
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values.package, values.main_service?.type])

    useEffect(() => {
        fetchData()
    }, [values.package])

    const isDisabledWorker = !state.workerConfig.isApply || (state.workerConfig.isApply && state.workerConfig.isRequireMatchWorkerNumber)

    return (
        <AccordionItem id={'need_service'} title={`${index}. ${t('CREATE_ORDER_DRAWER.SECTION_2.TITLE')}`}>
            <div className='need-service-section'>
                <p className='section-title'>{t('CREATE_ORDER_DRAWER.SECTION_2.SUB_TITLE')}</p>
                <FieldServiceInput
                    name={'service_selected'}
                    subSubPrices={values.subSubPrices}
                    serviceAvailable={values.service_available}
                    loading={state.loadingPackage}
                    rtl={rtl}
                    language={language}
                    label={{
                        mainService: t('CREATE_ORDER_DRAWER.SECTION_2.MAIN_SERVICES'),
                        subService: t('CREATE_ORDER_DRAWER.SECTION_2.SUB_SERVICES'),
                        subSubService: t('CREATE_ORDER_DRAWER.SECTION_2.SUB_SUB_SERVICES'),
                        offerService: t('CREATE_ORDER_DRAWER.SECTION_2.OFFER'),
                    }}
                    placeholder={{
                        mainService: t('CREATE_ORDER_DRAWER.SECTION_2.PLACEHOLDER_MAIN_SERVICE'),
                        subService: t('CREATE_ORDER_DRAWER.SECTION_2.PLACEHOLDER_SUB_SERVICE'),
                        subSubService: t('CREATE_ORDER_DRAWER.SECTION_2.PLACEHOLDER_SUB_SUB_SERVICE'),
                        offerService: t('CREATE_ORDER_DRAWER.SECTION_2.PLACEHOLDER_OFFER_SERVICE'),
                        noOptionsMessage: t('CREATE_ORDER_DRAWER.NO_OPTIONS'),
                    }}
                    onChangeMainService={e => {
                        if (e == undefined) {
                            return
                        }
                        else if (!Array.isArray(e))
                            onChangeMainService(e)
                        else {
                            setFieldValue('sub_service', e)
                        }
                    }}
                />
                {values.flow_type === FLOW_TYPE.PACKAGE &&
                    (<>
                        <p className='sub-title'>{t('CREATE_ORDER_DRAWER.ORDER_SOURCE')}</p>
                        <FieldSelector
                            name='order_source'
                            inputPlaceholder={t('CREATE_ORDER_DRAWER.SECTION_2.SELECT_ORDER_SOURCE')}
                            as='select'
                            inputGroup={<KTSVG defaultColor={true} path='/assets/images/icons/order-source.svg' />}
                            options={centroOrderSourceOption.filter(a => a.isActive === true).sort((a, b) => { return a.order - b.order })}
                            optionKey={(e) => e.value}
                            optionLabel={(e) => language === Languages.ar ? e.label_Ar : e.label_En}
                        />
                        <p className='sub-title'>{t('CREATE_ORDER_DRAWER.SECTION_2.PACKAGE')}</p>
                        <FieldSelector
                            name='package'
                            inputPlaceholder={t('CREATE_ORDER_DRAWER.SECTION_2.SEARCH_PACKAGE')}
                            inputGroup={state.loadingPackage ? <div className="spinner-border spinner-border-sm text-dark" role="status"></div> : <KTSVG defaultColor={true} path='/assets/images/icons/mail.svg' />}
                            options={values.listPackages}
                            optionKey={(e: PackageInfo) => e.id}
                            optionLabel={(e: PackageInfo) => language === Languages.ar ? e.titleAr : e.titleEn}
                            onInputChange={(inputValue: string, { action }: any) => {
                                if (action === 'input-change') {
                                    handleSearchPackage(inputValue);
                                }
                            }}
                        />
                        <p className='sub-title'>{t('CREATE_ORDER_DRAWER.SECTION_2.NUMBER_OF_WORKERS')}</p>
                        <div style={{ display: 'flex', flexDirection: 'row' }}>
                            <FieldTextInput
                                name='number_of_workers'
                                type='number'
                                placeholder=''
                                disabledInput={state.loadingPackage || isDisabledWorker}
                                inputGroup={
                                    values.loadingType === "worker" ? <div className="spinner-border spinner-border-sm text-dark" role="status"></div> :
                                        <KTSVG defaultColor={true} path='/assets/images/icons/users-four.svg' />
                                }
                                inputGroupEnd={<span className='text-muted'>{`${t('MAXIMUM')} ${values.maxWorker}`}</span>}
                                styleContainer={{ flex: 1 }}
                                //@ts-ignore
                                onChangeMore={(value: string) => {
                                    if (value) {
                                        if (values.maxWorker && Number(value) <= values.maxWorker) {
                                            onChangeNumberWorker(Number(value))
                                        }
                                    }
                                }}
                                className='number-worker--input'
                                allowDecimal={false}
                            />
                            <div className='d-flex'>
                                <Button
                                    className='btn-adjust minus'
                                    onClick={() => {
                                        onChangeNumberWorker((values.number_of_workers || 0) - 1)
                                    }

                                    }
                                    disabled={values.number_of_workers <= 0 || isDisabledWorker || state.loadingPackage}
                                >
                                    <i className='fa fa-regular fa-minus'></i>
                                </Button>
                                <Button
                                    className='btn-adjust plus'
                                    onClick={() => {
                                        onChangeNumberWorker((values.number_of_workers || 0) + 1)
                                    }
                                    }
                                    disabled={values.number_of_workers >= Number(values.maxWorker) || isDisabledWorker || state.loadingPackage}
                                >
                                    <i className='fa fa-regular fa-plus'></i>
                                </Button>
                            </div>
                        </div>
                        <Row className='mb-3 price-row'>
                            <Col className={rtl ? 'none-padding-col--right' : 'none-padding-col--left'}>
                                <p className='sub-title'>{t('CREATE_ORDER_DRAWER.SECTION_2.PRICE_BEFORE_VAT')}</p>
                                <FieldTextInput
                                    name='without_vat'
                                    placeholder={t('WITHOUT_VAT')}
                                    inputGroup={values.loadingType === "worker" ? <div className="spinner-border spinner-border-sm text-dark" role="status"></div> : <KTSVG defaultColor={true} path='/assets/images/icons/cost.svg' />}
                                    //@ts-ignore
                                    onChangeMore={(value: string) => {
                                        setValues({
                                            ...values,
                                            total_price: ((Number(value) || 0) * (1 + values.percentVAT)).toFixed(2),
                                            vat: ((Number(value) || 0) * values.percentVAT).toFixed(2)
                                        })
                                    }}
                                /></Col>
                            <Col className='none-padding-col'>
                                <p className='sub-title'>{t('CREATE_ORDER_DRAWER.SECTION_2.PRICE_VAT')}</p>
                                <FieldTextInput
                                    name='vat'
                                    placeholder={t('VAT')}
                                    disabledInput
                                />
                            </Col>
                            <Col className={rtl ? 'none-padding-col--left' : 'none-padding-col--right'}>
                                <p className='sub-title'>{t('CREATE_ORDER_DRAWER.SECTION_2.PRICE_WITH_VAT')}</p>
                                <FieldTextInput
                                    name='total_price'
                                    placeholder={t('TOTAL_PRICE')}
                                    inputGroupEnd={<span className='text-muted'>{currency}</span>}
                                    //@ts-ignore
                                    onChangeMore={(value: string) => {
                                        setValues({
                                            ...values,
                                            without_vat: ((Number(value) || 0) / (1 + values.percentVAT)).toFixed(2),
                                            vat: ((Number(value) || 0) / (1 + values.percentVAT) * values.percentVAT).toFixed(2)
                                        })
                                    }}
                                />
                            </Col>
                        </Row>

                        <p className='sub-title'>{t('CREATE_ORDER_DRAWER.PAYMENT_METHOD')}</p>
                        <FieldSelector
                            name='payment_method'
                            inputPlaceholder={t('CREATE_ORDER_DRAWER.SECTION_2.SELECT_PAYMENT_METHOD')}
                            inputGroup={<KTSVG defaultColor={true} path='/assets/images/icons/payment-method.svg' />}
                            options={PaymentMethod}
                            optionKey={(e) => e.value}
                            optionLabel={(e) => language === Languages.ar ? e.label_Ar : e.label_En}
                        />
                        {isInvoicePayment && (
                            <div className='invoice-upload'>
                                <p className='invoice-upload__title'>
                                    <span className='invoice-upload__help-text'>
                                        {t('CREATE_ORDER_DRAWER.INVOICE_UPLOAD_HELP')}
                                    </span>{' '}
                                    <span>{t('CREATE_ORDER_DRAWER.INVOICE_UPLOAD_HELP_FORMAT')}</span>
                                </p>
                                <div
                                    className={`invoice-upload__dropzone ${invoiceFileError ? 'has-error' : ''} ${values.invoiceFile ? 'has-file' : ''}`}
                                    onClick={() => invoiceInputRef.current?.click()}
                                >
                                    {!values.invoiceFile && (
                                        <div className='invoice-upload__placeholder'>
                                            <KTSVG defaultColor={true} path='/media/svg/files/invoice-upload.svg' />
                                            <span>{t('CREATE_ORDER_DRAWER.INVOICE_UPLOAD_TITLE')}</span>
                                        </div>
                                    )}
                                    {!!values.invoiceFile && (
                                        <div className='invoice-upload__preview'>
                                            <div className='invoice-upload__preview-card'>
                                                {invoicePreviewType === 'image' && (
                                                    <img src={invoicePreviewUrl} alt='invoice-preview' />
                                                )}
                                                {invoicePreviewType === 'pdf' && (
                                                    <KTSVG defaultColor={true} path='/media/svg/files/pdf_uploader.svg' />
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    <input
                                        ref={invoiceInputRef}
                                        type='file'
                                        accept='image/jpeg,image/png,application/pdf'
                                        className='d-none'
                                        onChange={handleInvoiceFileChange}
                                    />
                                </div>
                                {invoiceFileError && (
                                    <div className='invoice-upload__error'>{invoiceFileError}</div>
                                )}
                                {!!values.invoiceFile && (
                                    <div className='invoice-upload__actions'>
                                        <button
                                            type='button'
                                            className='invoice-upload__action invoice-upload__action--remove'
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                clearInvoiceFile()
                                            }}
                                        >
                                            <i className='fa fa-trash' aria-hidden='true'></i>
                                            <span>{t('CREATE_ORDER_DRAWER.INVOICE_UPLOAD_REMOVE')}</span>
                                        </button>
                                        <button
                                            type='button'
                                            className='invoice-upload__action invoice-upload__action--view'
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                handleViewInvoiceFile()
                                            }}
                                        >
                                            <i className='fa fa-eye' aria-hidden='true'></i>
                                            <span>{t('CREATE_ORDER_DRAWER.INVOICE_UPLOAD_VIEW')}</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                        <p className='sub-title'>{t('CREATE_ORDER_DRAWER.PAYMENT_STATUS')}</p>
                        <FieldSelector
                            name='payment_status'
                            inputPlaceholder={t('CREATE_ORDER_DRAWER.SECTION_2.SELECT_PAYMENT_STATUS')}
                            as='select'
                            inputGroup={<KTSVG defaultColor={true} path='/assets/images/icons/cost.svg' />}
                            options={PaymentStatus}
                            optionKey={(e) => e.value}
                            optionLabel={(e) => language === Languages.ar ? e.label_Ar : e.label_En}
                            inputDisabled={isOnlinePaymentLinkSms}
                        />
                    </>)}

            </div>
        </AccordionItem>
    )
}

export default NeedServiceComponent
